import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Divider,
  InputAdornment,
  FormHelperText,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import packageService from '../services/packageService';
import routerService from '../services/routerService';
import spopService from '../services/spopService';

interface AddPackageDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (message: string) => void;
}

interface PackageFormData {
  name: string;
  profile_name: string;
  price: string;
  validity_days: string;
  quota_bytes: string;
  burst_limit: string;
  burst_threshold: string;
  burst_time: string;
  priority: string;
  status: 'active' | 'inactive';
  description: string;
  spop_id: string;
}

const initialFormData: PackageFormData = {
  name: '',
  profile_name: '',
  price: '',
  validity_days: '30',
  quota_bytes: '',
  burst_limit: '',
  burst_threshold: '',
  burst_time: '',
  priority: '8',
  status: 'active',
  description: '',
  spop_id: '',
};

const AddPackageDialog: React.FC<AddPackageDialogProps> = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<PackageFormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<PackageFormData>>({});
  const [selectedRouter, setSelectedRouter] = useState<any>(null);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [availableProfiles, setAvailableProfiles] = useState<any[]>([]);
  const queryClient = useQueryClient();

  const createPackageMutation = useMutation({
    mutationFn: (data: any) => packageService.createPackage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packages'] });
      onSuccess?.('Paket berhasil ditambahkan');
      handleClose();
    },
    onError: (error: any) => {
      console.error('Error creating package:', error);
    },
  });

  // Get routers query
  const { data: routersData } = useQuery({
    queryKey: ['routers'],
    queryFn: () => routerService.getRouters(1, 100),
    enabled: open,
  });

  // Get SPOPs query
  const { data: spopsData } = useQuery({
    queryKey: ['spops-all'],
    queryFn: async () => {
      const response = await spopService.getAllSPOPs();
      return response.data.data.spops;
    },
    enabled: open,
  });

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    setSelectedRouter(null);
    setAvailableProfiles([]);
    onClose();
  };

  const handleInputChange = (field: keyof PackageFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSelectChange = (field: keyof PackageFormData) => (
    event: any
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<PackageFormData> = {};

    console.log('Validating form with data:', formData);

    if (!formData.name.trim()) {
      newErrors.name = 'Nama paket wajib diisi';
    }

    if (!formData.profile_name.trim()) {
      newErrors.profile_name = 'Nama profile wajib diisi';
    }

    if (!formData.price.trim()) {
      newErrors.price = 'Harga wajib diisi';
    } else if (isNaN(Number(formData.price)) || Number(formData.price) <= 0) {
      newErrors.price = 'Harga harus berupa angka positif';
    }

    if (!formData.validity_days.trim()) {
      newErrors.validity_days = 'Masa berlaku wajib diisi';
    } else if (isNaN(Number(formData.validity_days)) || Number(formData.validity_days) <= 0) {
      newErrors.validity_days = 'Masa berlaku harus berupa angka positif';
    }

    if (formData.quota_bytes && (isNaN(Number(formData.quota_bytes)) || Number(formData.quota_bytes) < 0)) {
      newErrors.quota_bytes = 'Kuota harus berupa angka positif';
    }

    if (formData.priority && (isNaN(Number(formData.priority)) || Number(formData.priority) < 1 || Number(formData.priority) > 8)) {
      newErrors.priority = 'Prioritas harus antara 1-8';
    }

    console.log('Validation errors found:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    console.log('Submit clicked, form data:', formData);
    console.log('Current errors:', errors);
    
    if (!validateForm()) {
      console.log('Validation failed, not submitting');
      return;
    }

    console.log('Validation passed, preparing submit data');
    const submitData = {
      name: formData.name.trim(),
      profile_name: formData.profile_name.trim(),
      bandwidth_upload: null,
      bandwidth_download: null,
      price: Number(formData.price),
      validity_days: Number(formData.validity_days),
      quota_bytes: formData.quota_bytes
        ? Math.round(Number(formData.quota_bytes) * 1024 * 1024 * 1024)
        : null,
      burst_limit: formData.burst_limit.trim() || null,
      burst_threshold: formData.burst_threshold.trim() || null,
      burst_time: formData.burst_time.trim() || null,
      priority: Number(formData.priority),
      status: formData.status,
      description: formData.description.trim() || null,
      spop_id: formData.spop_id || null,
    };

    console.log('Submitting data:', submitData);
    createPackageMutation.mutate(submitData);
  };

  const formatCurrency = (value: string) => {
    const num = value.replace(/\D/g, '');
    return new Intl.NumberFormat('id-ID').format(Number(num));
  };

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, price: value }));
    
    if (errors.price) {
      setErrors(prev => ({ ...prev, price: undefined }));
    }
  };

  // Handle router selection and profile sync
  const handleRouterSelect = async (router: any) => {
    setSelectedRouter(router);
    setLoadingProfiles(true);
    setAvailableProfiles([]);
    setFormData(prev => ({ ...prev, profile_name: '' })); // Reset profile selection
    
    try {
      const profiles = await routerService.getPPPProfiles(router.id);
      setAvailableProfiles(profiles);
      console.log('Available profiles from router:', profiles);
    } catch (error) {
      console.error('Error fetching PPP profiles:', error);
      setAvailableProfiles([]);
    } finally {
      setLoadingProfiles(false);
    }
  };

  const handleProfileSelect = (profileName: string) => {
    setFormData(prev => ({ ...prev, profile_name: profileName }));
    if (errors.profile_name) {
      setErrors(prev => ({ ...prev, profile_name: undefined }));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Tambah Paket Internet</Typography>
      </DialogTitle>
      
      <DialogContent dividers>
        {createPackageMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Gagal menambahkan paket: {createPackageMutation.error?.message || 'Terjadi kesalahan'}
          </Alert>
        )}
        
        <Stack spacing={3}>
          {/* Informasi Dasar */}
          <Box>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              Informasi Dasar
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Stack spacing={2}>
              <Box display="flex" gap={2}>
                <TextField
                  fullWidth
                  label="Nama Paket"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  error={!!errors.name}
                  helperText={errors.name}
                  placeholder="contoh: Paket Internet 20Mbps"
                />
              </Box>

              {/* SPOP Selection */}
              <Box>
                <FormControl fullWidth>
                  <InputLabel>SPOP (Opsional)</InputLabel>
                  <Select
                    value={formData.spop_id}
                    label="SPOP (Opsional)"
                    onChange={handleSelectChange('spop_id')}
                  >
                    <MenuItem value="">
                      <em>-- Semua SPOP --</em>
                    </MenuItem>
                    {spopsData?.map((spop: any) => (
                      <MenuItem key={spop.id} value={spop.id}>
                        {spop.name} - {spop.areaCode}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Pilih SPOP tertentu jika paket ini khusus untuk area tersebut, atau kosongkan untuk semua SPOP
                  </FormHelperText>
                </FormControl>
              </Box>

              {/* Router Selection */}
              <Box>
                <FormControl fullWidth>
                  <InputLabel>Pilih Router MikroTik</InputLabel>
                  <Select
                    value={selectedRouter ? selectedRouter.id : ''}
                    label="Pilih Router MikroTik"
                    onChange={(e) => {
                      const routerId = e.target.value;
                      const router = routersData?.data?.routers?.find((r: any) => r.id === routerId);
                      if (router) handleRouterSelect(router);
                    }}
                    disabled={loadingProfiles}
                  >
                    <MenuItem value="">
                      <em>-- Pilih Router --</em>
                    </MenuItem>
                    {routersData?.data?.routers?.map((router: any) => (
                      <MenuItem key={router.id} value={router.id}>
                        {router.name} ({router.ip_address}:{router.port})
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {loadingProfiles ? 'Memuat profile PPP...' : 'Pilih router untuk mengambil daftar profile PPP'}
                  </FormHelperText>
                </FormControl>
              </Box>

              {/* Profile Selection */}
              <Box>
                <FormControl fullWidth disabled={!selectedRouter || loadingProfiles}>
                  <InputLabel>Profile PPP</InputLabel>
                  <Select
                    value={formData.profile_name}
                    label="Profile PPP"
                    onChange={(e) => handleProfileSelect(e.target.value)}
                    error={!!errors.profile_name}
                  >
                    <MenuItem value="">
                      <em>-- Pilih Profile --</em>
                    </MenuItem>
                    {availableProfiles.map((profile) => (
                      <MenuItem key={profile.id} value={profile.name}>
                        {profile.name}
                        {profile.rateLimit && ` (${profile.rateLimit})`}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error={!!errors.profile_name}>
                    {errors.profile_name || 
                     (selectedRouter ? 
                       (availableProfiles.length > 0 ? 
                         `${availableProfiles.length} profile tersedia` : 
                         'Tidak ada profile ditemukan'
                       ) : 
                       'Pilih router terlebih dahulu'
                     )
                    }
                  </FormHelperText>
                </FormControl>
              </Box>

              {/* Manual Profile Input (fallback) */}
              <Box>
                <TextField
                  fullWidth
                  label="Atau Input Manual Profile Name"
                  value={formData.profile_name}
                  onChange={handleInputChange('profile_name')}
                  placeholder="contoh: profile-20mbps"
                  helperText="Jika profile tidak ada di dropdown, Anda bisa input manual"
                />
              </Box>
              
              <TextField
                fullWidth
                label="Deskripsi"
                value={formData.description}
                onChange={handleInputChange('description')}
                multiline
                rows={2}
                placeholder="Deskripsi tambahan untuk paket ini"
              />
            </Stack>
          </Box>

          {/* Harga dan Konfigurasi */}
          <Box>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              Harga dan Konfigurasi
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Stack spacing={2}>
              <Box display="flex" gap={2}>
                <TextField
                  fullWidth
                  label="Harga Paket"
                  value={formatCurrency(formData.price)}
                  onChange={handlePriceChange}
                  error={!!errors.price}
                  helperText={errors.price}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">Rp</InputAdornment>,
                  }}
                />
                <TextField
                  fullWidth
                  label="Masa Berlaku"
                  type="number"
                  value={formData.validity_days}
                  onChange={handleInputChange('validity_days')}
                  error={!!errors.validity_days}
                  helperText={errors.validity_days}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">hari</InputAdornment>,
                  }}
                />
              </Box>

              <Box display="flex" gap={2}>
                <TextField
                  fullWidth
                  label="Kuota Data (Opsional)"
                  type="number"
                  value={formData.quota_bytes}
                  onChange={handleInputChange('quota_bytes')}
                  error={!!errors.quota_bytes}
                  helperText={errors.quota_bytes || "Kosongkan untuk unlimited"}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">GB</InputAdornment>,
                  }}
                />
                
                <FormControl fullWidth>
                  <InputLabel>Prioritas</InputLabel>
                  <Select
                    value={formData.priority}
                    label="Prioritas"
                    onChange={handleSelectChange('priority')}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                      <MenuItem key={num} value={num.toString()}>
                        {num} {num === 1 ? '(Tertinggi)' : num === 8 ? '(Terendah)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.priority && (
                    <FormHelperText error>{errors.priority}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={handleSelectChange('status')}
                >
                  <MenuItem value="active">Aktif</MenuItem>
                  <MenuItem value="inactive">Non-aktif</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={createPackageMutation.isPending}>
          Batal
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={createPackageMutation.isPending}
        >
          {createPackageMutation.isPending ? 'Menyimpan...' : 'Simpan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPackageDialog;
